import { useState, useEffect, useRef } from 'react';
import { detectLogoColor } from '../utils/image/detect-logo-color';

/**
 * Hook to detect if dark mode is currently active
 */
function useDarkMode(): boolean {
  const [isDarkMode, setIsDarkMode] = useState(() => {
    if (typeof window === 'undefined') return false;
    return document.documentElement.classList.contains('dark');
  });

  useEffect(() => {
    // Check initial state
    const checkDarkMode = () => {
      setIsDarkMode(document.documentElement.classList.contains('dark'));
    };

    // Watch for changes to the dark class
    const observer = new MutationObserver(checkDarkMode);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class'],
    });

    // Also listen to media query changes (for system theme)
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleMediaChange = () => {
      // Only update if theme is set to 'system'
      // We check the class directly instead
      checkDarkMode();
    };
    mediaQuery.addEventListener('change', handleMediaChange);

    return () => {
      observer.disconnect();
      mediaQuery.removeEventListener('change', handleMediaChange);
    };
  }, []);

  return isDarkMode;
}

/**
 * Hook to detect if a logo needs a background color based on its content.
 * Returns the appropriate background color/style for the logo container.
 * 
 * @param imageSrc - The source URL of the logo image
 * @returns Object with background color information
 */
export function useLogoBackground(
  imageSrc: string | undefined
): {
  backgroundColor: string | undefined;
  needsBackground: boolean;
} {
  const isDarkMode = useDarkMode();
  const [needsBackground, setNeedsBackground] = useState(false);
  const [backgroundColor, setBackgroundColor] = useState<string | undefined>(undefined);
  const analyzedSrcRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (!imageSrc || imageSrc === analyzedSrcRef.current) {
      return;
    }

    // Reset state when image source changes
    setNeedsBackground(false);
    setBackgroundColor(undefined);
    analyzedSrcRef.current = undefined;

    // Create a new image element to analyze
    const img = new Image();
    
    // Only set crossOrigin if the image is from a different origin
    // This prevents CORS errors on localhost when loading production images
    try {
      if (typeof window !== 'undefined' && imageSrc) {
        const imageUrl = new URL(imageSrc, window.location.origin);
        const currentOrigin = window.location.origin;
        // Only set crossOrigin if it's a different origin
        if (imageUrl.origin !== currentOrigin) {
          img.crossOrigin = 'anonymous';
        }
      }
    } catch (e) {
      // If URL parsing fails (e.g., relative URL), don't set crossOrigin
      // This is safe - same-origin images don't need it
    }
    
    const handleLoad = () => {
      try {
        const result = detectLogoColor(img);
        
        // Determine if background is needed based on logo type and theme:
        // - White logos always need a background (dark bg on light theme, light bg on dark theme)
        // - Dark logos only need a background on dark themes (light bg), not on light themes (already visible)
        const shouldHaveBackground = result.isWhite || (result.isDark && isDarkMode);
        
        if (shouldHaveBackground) {
          setNeedsBackground(true);
          
          // Determine background color based on logo type and theme
          if (result.isWhite) {
            // White logo on transparent background + light theme -> dark slate background
            // White logo on transparent background + dark theme -> light background
            setBackgroundColor(isDarkMode ? '#f3f4f6' : '#1f2937'); // gray-100 in dark mode, gray-800 (dark slate) in light mode
          } else if (result.isDark) {
            // Dark/black logo on transparent background + dark theme -> off white background
            // Dark/black logo on transparent background + light theme -> no background (already visible)
            setBackgroundColor(isDarkMode ? '#f9fafb' : undefined); // gray-50 (off white) in dark mode, no background in light mode
          }
        } else {
          setNeedsBackground(false);
          setBackgroundColor(undefined);
        }
        
        analyzedSrcRef.current = imageSrc;
      } catch (error) {
        // Silently fail if CORS blocks the analysis (e.g., on localhost with production images)
        // This is expected behavior and doesn't need to be logged
        setNeedsBackground(false);
        setBackgroundColor(undefined);
      }
    };

    const handleError = () => {
      setNeedsBackground(false);
      setBackgroundColor(undefined);
    };

    img.addEventListener('load', handleLoad);
    img.addEventListener('error', handleError);
    img.src = imageSrc;

    return () => {
      img.removeEventListener('load', handleLoad);
      img.removeEventListener('error', handleError);
    };
  }, [imageSrc, isDarkMode]);

  return {
    backgroundColor,
    needsBackground,
  };
}
